import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { DEFAULT_SCOPE, registerContainer, unregisterContainer } from './registerScope';
import { ContainerProps, ContainerRef, FnContainerGetInstance, FnContainerHasInstance, FnContainerRemove, FnInstanceCreator, FnResolve, FnResolveWithId, Instance } from './types';

export const ModalContainer = forwardRef<ContainerRef, ContainerProps>(({ scope = DEFAULT_SCOPE, enterTimeout = 50, exitTimeout = 500, mode = 'append', onOpen, onResolve, onRemove }, ref) => {
  const enterTimeoutRef = useRef(enterTimeout);
  const exitTimeoutRef = useRef(exitTimeout);
  const modeRef = useRef(mode);
  const onOpenRef = useRef(onOpen);
  const onResolveRef = useRef(onResolve);
  const onRemoveRef = useRef(onRemove);

  const [instances, setInstances] = useState<Record<string, Instance>>({});
  const [stack, setStack] = useState<string[]>([]);

  const resolve: FnResolveWithId = useCallback(
    (id) => {
      const instance = instances[id];
      instance?.resolve();
    },
    [instances]
  );

  const resolveAll: FnResolve = useCallback(() => {
    Object.values(instances).forEach((instance) => {
      instance.resolve();
    });
  }, [instances]);

  const hasInstance: FnContainerHasInstance = useCallback(
    (id) => {
      return stack.includes(id);
    },
    [stack]
  );

  const getInstance: FnContainerGetInstance = useCallback(
    (id) => {
      return instances[id];
    },
    [instances]
  );

  const remove: FnContainerRemove = useCallback((id, options) => {
    setStack((prev) => prev.filter((it) => it !== id));
    setTimeout(() => {
      setInstances((instances) => {
        const { [id]: _, ...omitHash } = instances;
        return omitHash;
      });
    }, options?.exitTimeout);
    onRemoveRef.current?.(id);
  }, []);
  const removeRef = useRef(remove);

  const create: FnInstanceCreator = useCallback(
    (Component, options = {}, instanceProps) =>
      new Promise((res) => {
        const id = instanceProps?.id || v4();
        const instanceOptions = {
          enterTimeout: enterTimeoutRef.current,
          exitTimeout: exitTimeoutRef.current,
          instanceId: id,
          ...options
        };
        const instance: Instance = {
          Component,
          props: { ...options, ...instanceProps },
          resolve: (value) => {
            removeRef.current(id, instanceOptions);
            res(value);
            onResolveRef.current?.(value, id);
          },
          ...instanceOptions
        };
        setInstances((prev) => {
          if (modeRef.current === 'append') return { ...prev, [id]: instance };
          else return { [id]: instance, ...prev };
        });
        setTimeout(() => {
          setStack((prev) => [...prev, id]);
          onOpenRef.current?.(id, instance);
        }, instanceOptions.enterTimeout);
      }),
    []
  );

  useEffect(() => {
    enterTimeoutRef.current = enterTimeout;
    exitTimeoutRef.current = exitTimeout;
    modeRef.current = mode;
    onOpenRef.current = onOpen;
    onResolveRef.current = onResolve;
    onRemoveRef.current = onRemove;
    removeRef.current = remove;
  });

  useImperativeHandle(ref, () => ({
    create,
    resolve,
    resolveAll,
    hasInstance,
    getInstance
  }));

  useEffect(() => {
    registerContainer(scope, {
      create,
      resolve,
      resolveAll,
      hasInstance,
      getInstance
    });
    return () => unregisterContainer(scope);
  }, [create, getInstance, hasInstance, resolve, resolveAll, scope]);

  const mapKeys = useMemo(() => {
    const keys = Object.keys(instances);
    return keys.map((key) => {
      const { Component, props, resolve } = instances[key];
      return (
        <Component
          {...props}
          key={key}
          isOpen={stack.includes(key)}
          onResolve={resolve}
        />
      );
    });
  }, [instances, stack]);

  return <React.Fragment>{mapKeys}</React.Fragment>;
});

ModalContainer.displayName = 'ModalContainer';
